import {
  ApplicationConfig,
  importProvidersFrom,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  inject,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ICONOS_PICKER_DEFINICIONES, proveerI18n, proveerOverlays } from '@intelishs-group/ui';
import { provideNzIcons } from 'ng-zorro-antd/icon';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { AuthService, AuthSesionService } from '@auth';
import {
  SESION_CONFIG,
  type SesionConfig,
  credencialesInterceptor,
  sesionExpiradaInterceptor,
} from '@intelishs-group/session-core';
import { NotifyService } from '@intelishs-group/notify-service';
import { AccesosService } from '@services/accesos.service';
import { AccesosHttpService } from '@services/accesos-http.service';
import { environment } from 'src/environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideBrowserGlobalErrorListeners(),
    provideAnimationsAsync(),
    // `NzModalService` NO es `providedIn: 'root'`: lo provee `NzModalModule`. Aqui
    // nadie lo proveia — llegaba DE REBOTE del `<intelishs-file-viewer>` que
    // `app.html` monta en la raiz, porque ese componente importa el modulo en su
    // propio `imports` y eso iza el provider al injector de entorno.
    //
    // Funciona hasta que alguien quita el visor de `app.html`, y entonces se cae
    // algo que no tiene nada que ver con ese cambio. A `ffacturacion` le paso
    // exactamente eso al borrar su `modal-pdf-viewer` local: `tour-button` se
    // quedo sin el servicio que inyecta y que nunca importo el modulo.
    importProvidersFrom(NzModalModule),

    // `credencialesInterceptor` (session-core >= 5.2.14, sustituye a la copia
    // local) va PRIMERO: pone `withCredentials` en cada peticion, y sin el la
    // cookie HttpOnly de sesion no se adjunta. Este front no llama a terceros,
    // asi que `hostsSinCredenciales` no se configura.
    // `sesionExpiradaInterceptor` convierte el 401 del backend en navegacion a
    // /login.
    provideHttpClient(withInterceptors([credencialesInterceptor, sesionExpiradaInterceptor])),

    // Los overlays del CDK vuelven al apilamiento por z-index (ui >= 5.2.7). Sin
    // esto, desde Angular 21 un modal abierto tapa los avisos de SweetAlert y el
    // velo de carga: salen detras, no se pueden pulsar y la pantalla parece colgada.
    proveerOverlays(),
    // Idioma de la plataforma (ui >= 5.2.10): registra `es-MX`, provee `LOCALE_ID` y
    // el i18n de ng-zorro con su `locale` en `es-MX` desde UN valor. Sustituye a las
    // tres lineas a mano, cuya version con `es_ES` a secas tronaba el date picker con
    // `NG0701` sobre `"es"` (lo destapo el rango de fechas de la bitacora).
    proveerI18n(),

    // ng-zorro NO dibuja un icono que no este en el inyector, y uno elegido en
    // tiempo de EJECUCION —el de una aplicacion, un modulo o un rol— no se puede
    // resolver de otro modo. Son los 447 outline del paquete en 15 grupos, asi
    // que cubren tambien los `nzType` literales de las plantillas: no hace falta
    // una segunda lista para el chrome.
    provideNzIcons(ICONOS_PICKER_DEFINICIONES),

    // En produccion NO se cae al proveedor simulado: se lanza. Un build
    // productivo que aceptara la credencial de desarrollo es un incidente, no un
    // El ÚNICO proveedor de autenticación. Hubo uno simulado —`demo/demo`— que
    // existió para poder trabajar sin catálogo de usuarios, y se retiró en
    // cuanto el real funcionó.
    //
    // No por limpieza: un simulado que rellena huecos por otra vía NO los
    // cubre, los ESCONDE. Éste tapó tres, y los tres aparecieron el día que se
    // apagó — `nombre` y `correo` que el token no emitía, esos mismos que no se
    // conservaban al refrescar, y el rol del menú, que filtraba por
    // `administrador` porque era el literal que el simulado emitía. Con el real
    // el token trae `portal:superadmin` y el riel salía VACÍO: sin error, sin
    // ruta rota, sin nada en consola.
    // La sesion la emite IDENTIDAD y este portal solo la lee. Antes emitia la
    // suya —`AuthHttpService`— y el superadministrador acababa con dos sesiones
    // distintas segun por donde entrara.
    { provide: AuthService, useClass: AuthSesionService },

    // Se provee la CONFIGURACION, no `proveerSesion()`: ese registra su propio
    // inicializador y aqui ya hay uno. Dos leerian la sesion dos veces.
    //
    // ⚠️ Y por eso este repo es el UNICO donde el arreglo del retroceso tuvo que
    // vivir en el servicio y no en `proveerSesion`: colgado del proveedor, la
    // aplicacion donde se reporto el fallo era justo la que no lo recibia.
    //
    // Antes esto iba por `useFactory` para capturar `NotifyService`, porque la
    // flecha del aviso corre despues de un `await`, fuera del contexto de
    // inyeccion: su `inject()` lanzaba NG0203 y la libreria se lo tragaba. Desde
    // 5.0.5 el servicio lo invoca con `runInInjectionContext`, asi que un
    // `useValue` a secas basta.
    {
      provide: SESION_CONFIG,
      useValue: {
        identidadUrl: environment.identidadUrl,
        loginUrl: environment.loginUrl,

        // El aviso antes de mandar al login. Es obligatorio en la libreria, y
        // no por gusto: la primera version redirigia en silencio y se comio un
        // aviso que llevaba años en produccion.
        avisarSesionCaducada: () => inject(NotifyService).unauthorizedNotify(),
      } satisfies SesionConfig,
    },

    // El gestor de accesos habla con `AdminPortal.API` sobre el esquema `acceso`
    // de la base propia. `AccesosService` es abstracta —igual que
    // `AuthService`— asi que esta es la unica linea que distingue una
    // implementacion de otra.
    //
    // Hubo una implementacion `AccesosMockService` sobre `localStorage`,
    // mientras el esquema de la base se reestructuraba. Ya se BORRO: su unico
    // valor restante era la curaduria de iconos, nombres y descripciones de las
    // aplicaciones que faltaban, y esa quedo transcrita en la seccion 7 de
    // `alcance-aplicaciones-no-migradas.txt`.
    //
    // No se dejo tras un flag, y eso fue deliberado: dos implementaciones del
    // mismo contrato con las mismas reglas de negocio divergen a la primera
    // correccion, y un flag que nadie mueve es duplicacion permanente.
    { provide: AccesosService, useClass: AccesosHttpService },

    // Rehidrata la sesion ANTES del primer render: sin esto, un F5 dentro de la
    // app pinta el login por un instante y el parpadeo parece un cierre de
    // sesion espurio.
    provideAppInitializer(() => firstValueFrom(inject(AuthService).restaurarSesion())),
  ],
};
